import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  BRAND_COLORS,
  HERO_ASSETS,
  SUBJECTS,
  SUBJECT_THEME,
  type MeResponse,
  type ResponsePayload,
} from '@lk/shared';
import { BrandImg } from '../../components/BrandImg';
import { SafeSvg, looksLikeSvg } from '../../components/SafeSvg';
import { Button } from '../../components/Button';
import { Icon, SUBJECT_ICON } from '../../components/Icon';
import { Meter, Panel } from '../../components/Panel';
import { SyncIndicator } from '../../components/SyncIndicator';
import { AnimatePresence, Pop } from '../../components/Motion';
import { TimerRing } from '../../components/TimerRing';
import { LoadingState } from '../../components/States';
import { cn } from '../../lib/cn';
import { coinsLabel } from '../../lib/format';
import { humanMessage } from '../../lib/errors';
import { fetchHint } from '../../lib/api';
import { getDb } from '../../offline/db';
import { recordAttempt } from '../../offline/attempts';
import { attemptsForSession } from '../../offline/queue';
import { finishSession, loadSession, saveCursor } from '../../offline/sessions';
import { useLiveQuery } from '../../offline/useLiveQuery';
import { useSyncState } from '../../offline/useSync';
import { useQuizStore } from '../../store/quizStore';
import { playWindowQuery, walletQuery } from '../learner/queries';
import { speechTextOf, stripSpeechLines } from '../../audio/speech';
import { AnswerInput } from './AnswerInput';
import { ListenPanel } from './ListenPanel';
import { Feedback } from './Feedback';
import { draftToResponse, emptyDraft, isDraftReady, type Draft } from './draft';
import { estimateCoins, willZeroOutCoins } from './coins';
import type { QuizItem } from './types';

export function QuizScreen({
  sessionId,
  me,
}: {
  sessionId: string;
  me: MeResponse;
}): React.JSX.Element {
  const navigate = useNavigate();
  const learnerId = me.user.learnerId ?? me.learners[0]?.id ?? '';

  // Сесія і спроби читаються напряму з IndexedDB — тому F5 нічого не ламає.
  const stored = useLiveQuery(
    async () => (await loadSession(sessionId)) ?? null,
    [sessionId],
    undefined as Awaited<ReturnType<typeof loadSession>> | null | undefined,
  );
  const attempts = useLiveQuery(
    () => attemptsForSession(getDb(), sessionId),
    [sessionId],
    [] as Awaited<ReturnType<typeof attemptsForSession>>,
  );

  const phase = useQuizStore((s) => s.phase);
  const activeAttemptId = useQuizStore((s) => s.activeAttemptId);
  const questionStartedAt = useQuizStore((s) => s.questionStartedAt);
  const hintsUsed = useQuizStore((s) => s.hintsUsed);
  const hints = useQuizStore((s) => s.hints);
  const activeItemId = useQuizStore((s) => s.activeItemId);
  const submitting = useQuizStore((s) => s.submitting);
  const beginSubmit = useQuizStore((s) => s.beginSubmit);
  const beginSession = useQuizStore((s) => s.beginSession);
  const beginQuestion = useQuizStore((s) => s.beginQuestion);
  const showFeedback = useQuizStore((s) => s.showFeedback);
  const finishFeedback = useQuizStore((s) => s.finishFeedback);
  const addHint = useQuizStore((s) => s.addHint);

  const [mustRest, setMustRest] = useState(false);

  /*
   * Стан відрізка гри перечитуємо з СЕРВЕРА щоразу, коли сесія відкривається.
   * Копія, що приїхала колись разом із питаннями, застаріває вже за пів
   * години — а саме через півгодини й пізніше дитина повертається до
   * незавершеної сесії. Дивлячись у ту копію, екран бачив межу з обіду
   * і відправляв на перерву, якої насправді немає.
   */
  const playWindow = useQuery({
    ...playWindowQuery(learnerId),
    enabled: Boolean(learnerId),
    refetchOnMount: 'always',
  });
  /*
   * Годиться тільки відповідь, отримана ПІСЛЯ відкриття цього екрана. Кеш
   * запитів теж лежить в IndexedDB і переживає ніч, тому «є дані» тут не
   * означає «дані свіжі», а на застарілому вікні екран сам себе виганяє
   * на перерву. Доки свіжого немає (перший запит, офлайн, збій), межі не
   * застосовуємо взагалі: дограти вже видану сесію офлайн — дозволено.
   */
  const openedAt = useRef(Date.now());
  /*
   * Те саме серверне число приїжджає двома шляхами, і беремо ми СВІЖІШЕ.
   *
   * Опитування /api/play-window лишається основним джерелом (і саме воно
   * живить екран перерви), але ходить раз на 30 секунд. А відколи перерва
   * перестала приїжджати помилкою 403 на пачку спроб, сервер кладе стан
   * відрізка просто в тіло відповіді — тобто найточніше «досить» ми чуємо
   * рівно тоді, коли доїхала остання відповідь. Чекати на нього пів хвилини
   * означало б дати дитині відповідати в порожнечу.
   */
  const sync = useSyncState();
  const syncedAt = sync.lastSyncAt ?? 0;
  const fresherFromSync =
    sync.playWindow !== null && syncedAt >= Math.max(openedAt.current, playWindow.dataUpdatedAt);
  const serverWindow = fresherFromSync
    ? sync.playWindow
    : playWindow.dataUpdatedAt >= openedAt.current
      ? playWindow.data
      : undefined;
  const breakUntil = serverWindow?.breakUntil ?? null;
  const playUntil = serverWindow?.playUntil ?? null;

  const activeAttempt = useLiveQuery(
    async () => (activeAttemptId ? ((await getDb().attempts.get(activeAttemptId)) ?? null) : null),
    [activeAttemptId],
    null,
  );

  const items = useMemo(
    () => [...(stored?.payload.items ?? [])].sort((a, b) => a.position - b.position),
    [stored],
  );

  /**
   * Позиція визначається з ФАКТІВ у базі, а не зі змінної в памʼяті:
   * перше питання, на яке ще немає спроби. Тому перезавантаження сторінки
   * повертає дитину рівно туди, де вона зупинилася.
   */
  const answeredIds = useMemo(() => new Set(attempts.map((row) => row.itemId)), [attempts]);
  const nextIndex = items.findIndex((entry) => !answeredIds.has(entry.id));
  const nextItem = nextIndex >= 0 ? items[nextIndex] : undefined;

  /**
   * Щойно відповідь записана, `nextIndex` одразу перескакує на наступне питання.
   * Тому фідбек ЗАВЖДИ показуємо по явно запамʼятованому `activeItemId`, а не по
   * поточній позиції: інакше дитина бачила б розбір не того питання, а фідбек
   * останнього питання сесії взагалі не встигав би зʼявитися.
   */
  const answeredItem = activeItemId ? items.find((entry) => entry.id === activeItemId) : undefined;
  /** Тримаємо екран на відповіданому питанні і поки триває запис, і поки видно фідбек. */
  const holding = phase === 'feedback' || submitting;
  const item = holding ? (answeredItem ?? nextItem) : nextItem;
  const displayIndex =
    holding && answeredItem ? items.findIndex((entry) => entry.id === answeredItem.id) : nextIndex;
  /** Після цієї відповіді невідповіданих питань не лишається. */
  const isLast = holding ? nextIndex === -1 : nextIndex === items.length - 1;

  useEffect(() => {
    beginSession(sessionId);
  }, [sessionId, beginSession]);

  // Нове питання — новий відлік часу.
  useEffect(() => {
    if (phase === 'question' && nextItem) beginQuestion(nextItem.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextItem?.id, phase]);

  /*
   * Перерву призначає сервер. Якщо вона вже триває — забираємо екран, але
   * на СТИКУ питань, а не посеред відповіді: відібрати екран у дитини,
   * яка вже щось обрала, було б крадіжкою балів.
   */
  useEffect(() => {
    if (!breakUntil || phase !== 'question' || submitting) return;
    if (new Date(breakUntil).getTime() <= Date.now()) return;
    setMustRest(true);
  }, [breakUntil, phase, submitting]);

  /*
   * Межа відрізка гри. Сервер уже підрізав кількість питань під залишок часу,
   * але дитина може думати над кожним довше за оцінку — тоді сесія перетне
   * межу просто зараз, у нас на очах.
   */
  useEffect(() => {
    if (!playUntil || phase !== 'question' || submitting) return;
    const left = new Date(playUntil).getTime() - Date.now();
    /*
     * Межу, яка минула ще до відкриття сесії, самі не застосовуємо: перерву
     * призначає сервер, і поки він її не призначив (`breakUntil` порожній),
     * дитина має право дограти. Інакше «Продовжити» ганяло б по колу
     * арена → перерва → арена, бо на перерві не було б чого відлічувати.
     */
    if (left <= 0) return;
    // Прокидаємось рівно на межі: інакше дитина, яка сидить над питанням,
    // дізналася б про перерву лише після відповіді.
    const id = setTimeout(() => setMustRest(true), left);
    return () => clearTimeout(id);
  }, [playUntil, phase, submitting]);

  useEffect(() => {
    if (!mustRest || phase !== 'question' || submitting) return;
    /*
     * Сесію НЕ завершуємо: перерва — це пауза, а не кінець. Позначка
     * `finishedAt` ховала картку «Продовжити» назавжди, і дитина втрачала
     * решту питань разом із балами за них.
     */
    void navigate({ to: '/break' });
  }, [mustRest, phase, submitting, navigate]);

  // Усі питання пройдено (і фідбек останнього вже закрито) — на підсумок.
  // `submitting` тут критичний: запис у Dexie робить питання «відповіданим»
  // раніше, ніж встигне зʼявитися фідбек, і без прапорця останній розбір
  // просто зникав би за екраном підсумку.
  useEffect(() => {
    if (!stored || items.length === 0) return;
    if (nextIndex === -1 && phase === 'question' && !submitting) {
      void finishSession(sessionId);
      void navigate({ to: '/summary/$sessionId', params: { sessionId } });
    }
  }, [nextIndex, phase, submitting, stored, items.length, sessionId, navigate]);

  useEffect(() => {
    if (nextIndex > 0) void saveCursor(sessionId, nextIndex);
  }, [nextIndex, sessionId]);

  if (stored === undefined) return <LoadingState label="Відкриваємо сесію…" />;
  if (stored === null) return <SessionMissing />;
  if (!item) return <LoadingState label="Рахуємо підсумок…" />;

  return (
    <QuestionView
      key={item.id}
      item={item}
      index={displayIndex}
      total={items.length}
      learnerId={learnerId}
      sessionId={sessionId}
      isLast={isLast}
      phase={phase}
      questionStartedAt={questionStartedAt}
      timerRunning={phase === 'question' && activeItemId === item.id}
      hintsUsed={hintsUsed}
      hints={hints}
      attempt={activeAttempt ?? undefined}
      onHint={addHint}
      onSubmitStart={beginSubmit}
      onAnswered={(attemptId) => showFeedback(attemptId, item.id)}
      onNext={finishFeedback}
    />
  );
}

function SessionMissing(): React.JSX.Element {
  return (
    <main className="lk-shell lk-safe grid min-h-dvh place-items-center text-center">
      <Panel className="p-6">
        <Icon name="compass" size={72} className="mx-auto" />
        <h1 className="mt-2 text-2xl font-black">Сесію не знайдено</h1>
        <p className="mt-2 text-sm font-semibold text-white/60">
          Схоже, її грали на іншому пристрої. Почни нову — прогрес нікуди не подівся.
        </p>
        <a href="/arena" className="lk-btn lk-btn--gold mt-5 inline-flex">
          На арену
        </a>
      </Panel>
    </main>
  );
}

interface QuestionViewProps {
  item: QuizItem;
  index: number;
  total: number;
  learnerId: string;
  sessionId: string;
  isLast: boolean;
  phase: 'question' | 'feedback';
  questionStartedAt: number;
  /**
   * Чи вже стартував відлік саме для цього питання.
   *
   * Без цієї ознаки таймер на першому рендері брав `questionStartedAt`
   * з моменту ЗАВАНТАЖЕННЯ застосунку: ефекти дочірнього компонента в React
   * виконуються раніше за батьківські, тому `beginQuestion` ще не встиг
   * проставити час. Якщо дитина провела на екранах входу й вибору режиму
   * довше за ліміт питання, перше питання миттєво зараховувалось як таймаут.
   */
  timerRunning: boolean;
  hintsUsed: number;
  hints: string[];
  attempt: import('../../offline/db').QueuedAttempt | undefined;
  onHint: (text: string) => void;
  /** Викликається СИНХРОННО, ще до запису в Dexie. */
  onSubmitStart: () => void;
  onAnswered: (attemptId: string) => void;
  onNext: () => void;
}

function QuestionView({
  item,
  index,
  total,
  learnerId,
  sessionId,
  isLast,
  phase,
  questionStartedAt,
  timerRunning,
  hintsUsed,
  hints,
  attempt,
  onHint,
  onSubmitStart,
  onAnswered,
  onNext,
}: QuestionViewProps): React.JSX.Element {
  const navigate = useNavigate();
  const sync = useSyncState();
  const [draft, setDraft] = useState<Draft>(() => emptyDraft(item));
  const [now, setNow] = useState(() => Date.now());
  const submitting = useRef(false);
  const theme = SUBJECT_THEME[item.subject];
  const wallet = useQuery({ ...walletQuery(learnerId), enabled: Boolean(learnerId) });

  /**
   * Аудіозавдання. Рушій кладе фразу і в `passage`, і в `prompt` (друге —
   * заради унікального id питання), а на екран вона не має потрапити жодного
   * разу: інакше дитина прочитає відповідь замість того, щоб її почути.
   */
  const speechText = speechTextOf(item.passage);
  const promptText = speechText ? stripSpeechLines(item.prompt) : item.prompt;

  const hintMutation = useMutation({
    mutationFn: (nextIndex: number) => fetchHint({ sessionId, itemId: item.id, index: nextIndex }),
    onSuccess: (data) => onHint(data.text),
  });

  const submit = useCallback(
    async (response: ResponsePayload) => {
      if (submitting.current) return;
      submitting.current = true;
      // Повідомляємо екран ДО запису: інакше позиція, яка рахується з фактів
      // у базі, встигне перескочити на наступне питання раніше за фідбек.
      onSubmitStart();
      try {
        const row = await recordAttempt({
          learnerId,
          sessionId,
          itemId: item.id,
          response,
          // Стеля — ліміт питання: на таймауті це рівно ліміт, і жодне
          // розсинхронізоване значення не потрапить на сервер роздутим.
          responseMs: Math.min(
            item.timeLimitSec * 1000,
            Math.max(0, Date.now() - questionStartedAt),
          ),
          hintsUsed,
        });
        onAnswered(row.id);
      } finally {
        submitting.current = false;
      }
    },
    [learnerId, sessionId, item.id, item.timeLimitSec, questionStartedAt, hintsUsed, onAnswered, onSubmitStart],
  );

  // Таймер тикає раз на 250 мс — цього досить для плавного кільця й не гріє батарею.
  useEffect(() => {
    if (!timerRunning) return;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [timerRunning, item.id]);

  // Поки відлік не почався — показуємо повний час, а не «вже минув».
  const remaining = timerRunning
    ? item.timeLimitSec - (now - questionStartedAt) / 1000
    : item.timeLimitSec;

  // Час вийшов — фіксуємо як спробу-таймаут (контракт це передбачає окремо).
  useEffect(() => {
    if (!timerRunning) return;
    if (remaining > 0) return;
    void submit({ kind: 'timeout' });
  }, [remaining, timerRunning, submit]);

  const ready = isDraftReady(draft, item);
  const stake = estimateCoins({
    band: item.band,
    coinValue: item.coinValue,
    isCorrect: true,
    responseMs: 60_000,
    hintsUsed,
    hintsAvailable: item.hintCount,
    ...(wallet.data ? { earnedToday: wallet.data.earnedToday, dailyCap: wallet.data.dailyCap } : {}),
  });

  return (
    <main className="lk-shell lk-safe flex min-h-dvh flex-col gap-3 pb-8">
      <header className="flex items-center gap-3 pt-3">
        <button
          type="button"
          className="lk-chip shrink-0"
          onClick={() => void navigate({ to: '/arena' })}
          aria-label="Вийти на арену (прогрес збережеться)"
        >
          <Icon name="close" size={18} />
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between text-[11px] font-black text-white/60">
            <span className="tabular" data-testid="question-counter">
              Питання {index + 1} / {total}
            </span>
            <span className="truncate">{SUBJECTS[item.subject].title}</span>
          </div>
          <Meter
            className="mt-1"
            value={(index + (phase === 'feedback' ? 1 : 0)) / Math.max(1, total)}
            label="Прогрес сесії"
            from={theme.color}
            to={theme.accent}
          />
        </div>

        <SyncIndicator className="shrink-0" />
      </header>

      <div className="flex items-center gap-3">
        <BrandImg
          src={HERO_ASSETS[item.subject].path}
          alt={`Маскот: ${theme.name}`}
          fallbackIcon={SUBJECT_ICON[item.subject]}
          colors={[theme.color, theme.accent]}
          shape="rounded"
          className="h-16 w-16 shrink-0"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-black" style={{ color: theme.color }}>
            {theme.name} · {item.skillTitle}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span
              className="lk-chip text-[10px]"
              style={{
                borderColor: item.band === 'hard' ? BRAND_COLORS.red : BRAND_COLORS.green,
                color: item.band === 'hard' ? BRAND_COLORS.red : BRAND_COLORS.green,
              }}
              data-testid="band-chip"
            >
              <Icon name={item.band === 'hard' ? 'mode-challenge' : 'sprout'} size={12} />
              {item.band === 'hard' ? 'складне' : 'просте'}
            </span>
            <span className="lk-chip text-[10px] text-[var(--lk-gold)]">
              <Icon name="coin" size={12} />
              {coinsLabel(stake.coins)}
            </span>
            {hintsUsed > 0 ? (
              <span className="lk-chip text-[10px] text-white/60">підказок: {hintsUsed}</span>
            ) : null}
          </div>
        </div>
        {phase === 'question' ? (
          <TimerRing remaining={remaining} total={item.timeLimitSec} className="shrink-0" />
        ) : null}
      </div>

      <AnimatePresence mode="wait" initial={false}>
        {phase === 'feedback' ? (
          <Pop key={`feedback-${item.id}`} id={`feedback-${item.id}`}>
            <Feedback
              item={item}
              attempt={attempt}
              isLast={isLast}
              onNext={() => {
                setDraft(emptyDraft(item));
                onNext();
              }}
            />
          </Pop>
        ) : (
          <Pop key={`question-${item.id}`} id={`question-${item.id}`} className="flex flex-col gap-3">
          {speechText ? (
            // Аудіозавдання: текст фрази ховаємо за кнопкою, інакше дитина
            // прочитає відповідь замість того, щоб почути її.
            <ListenPanel text={speechText} />
          ) : item.passage ? (
            <Panel tight className="p-4">
              {looksLikeSvg(item.passage) ? (
                // Геометрію, розгортки й діаграми рушій віддає готовим SVG.
                // Без цієї гілки дитина бачила на екрані сирий код розмітки.
                // Ширину не обмежуємо: серед умов є довгі смуги 512×64
                // («продовж ряд»), і в рамці на 16rem фігури в них сідали до
                // 27 px — дитині нема чого роздивлятися. Квадратні малюнки це
                // не зачіпає: їх однаково стримує `max-h-56`.
                <SafeSvg
                  markup={item.passage}
                  label="Малюнок до питання"
                  className="mx-auto w-full [&>svg]:max-h-56"
                  itemId={item.id}
                  field="passage"
                />
              ) : (
                <p className="text-sm leading-relaxed font-semibold text-white/85">
                  {item.passage}
                </p>
              )}
            </Panel>
          ) : null}

          <Panel className="p-4">
            <h2 className="text-xl leading-snug font-black" data-testid="prompt">
              {promptText}
            </h2>
          </Panel>

          <AnswerInput item={item} draft={draft} onChange={setDraft} />

          {hints.length > 0 ? (
            <ul className="flex flex-col gap-2">
              {hints.map((text, hintIndex) => (
                <li
                  key={hintIndex}
                  className="lk-panel-tight flex items-start gap-2 border-[var(--lk-cyan)]/50 px-4 py-2.5 text-sm font-semibold"
                >
                  <Icon name="hint" size={16} className="mt-0.5" />
                  <span className="min-w-0 flex-1">{text}</span>
                </li>
              ))}
            </ul>
          ) : null}

          {hintMutation.isError ? (
            <p className="text-center text-xs font-bold text-[var(--lk-red)]" role="alert">
              {humanMessage(hintMutation.error)}
            </p>
          ) : null}

          <div className="mt-1 flex flex-col gap-2">
            {item.hintCount > hintsUsed ? (
              <Button
                tone="ghost"
                block
                loading={hintMutation.isPending}
                disabled={hintMutation.isPending || !sync.online}
                onClick={() => hintMutation.mutate(hintsUsed)}
                data-testid="hint-button"
              >
                <Icon name="hint" size={20} />
                Підказка ({item.hintCount - hintsUsed})
                {willZeroOutCoins(hintsUsed, item.hintCount) ? (
                  <span className="text-xs font-bold text-[var(--lk-red)]">
                    · остання: бал стане 0
                  </span>
                ) : null}
              </Button>
            ) : null}

            {!sync.online && item.hintCount > hintsUsed ? (
              <p className="text-center text-[11px] font-bold text-white/40">
                Підказки доступні лише онлайн — відповідай сам, це навіть цікавіше.
              </p>
            ) : null}

            <Button
              tone="gold"
              size="xl"
              block
              disabled={!ready}
              onClick={() => {
                const response = draftToResponse(draft);
                if (response) void submit(response);
              }}
              data-testid="submit-answer"
              className={cn(!ready && 'opacity-60')}
            >
              Відповісти
            </Button>
          </div>
          </Pop>
        )}
      </AnimatePresence>
    </main>
  );
}
